import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowLeft, Trophy, BarChart3, Target, Zap } from 'lucide-react'
import { useApp } from '../contexts/AppContext'
import Button from '../components/ui/Button'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Game2048 from '../components/games/Game2048/Game2048'

export default function Game2048Page() {
  const { getRecentResults } = useApp()
  const recentResults = getRecentResults('2048', 5)
  
  const stats = {
    totalGames: recentResults.length,
    bestScore: recentResults.length > 0 
      ? Math.max(...recentResults.map(r => r.score))
      : 0,
    averageScore: recentResults.length > 0 
      ? Math.round(recentResults.reduce((sum, r) => sum + r.score, 0) / recentResults.length)
      : 0,
    totalMoves: recentResults.reduce((sum, r) => sum + (r.moves || 0), 0),
    wins: recentResults.filter(r => r.winner === 'player').length
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button
              as={Link}
              to="/"
              variant="ghost"
              size="sm"
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl font-bold">2048</h1>
            <p className="text-text-muted mt-2">
              Slide numbered tiles to combine them and reach the 2048 tile.
            </p>
          </div>
        </div>
      </motion.div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Game Area - 2/3 width */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Game2048 />
          </motion.div>
        </div>
        
        {/* Stats Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Game Stats */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card shadow="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-secondary" />
                  <h2 className="text-xl font-bold">Game Statistics</h2>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Total Games</span>
                    <span className="font-semibold">{stats.totalGames}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Best Score</span>
                    <span className="font-semibold text-secondary">{stats.bestScore}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Average Score</span>
                    <span className="font-semibold">{stats.averageScore}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Total Moves</span>
                    <span className="font-semibold">{stats.totalMoves}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Games Won</span>
                    <span className="font-semibold text-success">{stats.wins}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Recent Games */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card shadow="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-secondary" />
                  <h2 className="text-xl font-bold">Recent Games</h2>
                </div>
              </CardHeader>
              <CardContent>
                {recentResults.length > 0 ? (
                  <div className="space-y-3">
                    {recentResults.map((result, index) => (
                      <div 
                        key={result.id || index}
                        className="flex items-center justify-between p-3 rounded-lg bg-surface-variant"
                      >
                        <div className="flex items-center gap-3">
                          {result.winner === 'player' ? (
                            <Target className="w-4 h-4 text-green-500" />
                          ) : (
                            <Zap className="w-4 h-4 text-secondary" />
                          )}
                          <div>
                            <div className="font-medium">
                              Score: {result.score}
                            </div>
                            <div className="text-sm text-text-muted">
                              {result.moves} moves • {result.duration}s
                            </div>
                          </div>
                        </div>
                        <div className={`font-bold ${result.winner === 'player' ? 'text-success' : ''}`}>
                          {result.winner === 'player' ? 'WIN' : 'LOSS'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-text-muted">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No games played yet</p>
                    <p className="text-sm mt-1">Play a game to see your results here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
          
          {/* How to Play */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card shadow="md">
              <CardHeader>
                <h2 className="text-xl font-bold">How to Play</h2>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-text-muted">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">1.</span>
                    <span>Use <strong>arrow keys</strong> or <strong>WASD</strong> to slide tiles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">2.</span>
                    <span>When two tiles with the same number touch, they merge into one</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">3.</span>
                    <span>Try to create a tile with the number <strong>2048</strong> to win</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">4.</span>
                    <span>Game ends when no more moves are possible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">5.</span>
                    <span>Press <strong>R</strong> to restart the game</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}